/**
 * DPL DEW — Financial Calculation Engine
 *
 * DESIGN: Fully generic — no hardcoded amounts or thresholds.
 * Payment schedule rules come from AppPreferences.payment_schedule_rules.
 * If no rules are configured, a simple proportional split is used.
 */

import type { PaymentMilestone, Financials } from '@/types/quotation';
import { DEFAULT_PAYMENT_MILESTONES } from '@/types/quotation';

export interface FinancialCalculationResult {
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  taxableAmount: number;
  grandTotal: number;
  paymentSchedule: PaymentMilestone[];
}

/**
 * Rule-based payment schedule calculator.
 *
 * Rules are defined in AppPreferences and have this shape:
 *   { upTo?: number; milestones: [{ name, pct, fixedCap? }] }
 *
 * If rules is empty/null the existing milestone percentages are preserved
 * and only the amounts are recalculated from grandTotal (manual mode).
 */
export interface PaymentScheduleRule {
  /** Applies when grandTotal <= upTo (undefined = catch-all) */
  upTo?: number;
  milestones: {
    name: string;
    /** Percentage of grandTotal */
    percentage: number;
    /** Hard cap in currency units (optional) */
    fixedCap?: number;
  }[];
}

/**
 * Core financial calculation.
 *
 * @param sections      Array of QuotationSection objects (need sectionSubtotal + gstEnabled)
 * @param financials    Current Financials object (rates, discount, tax)
 * @param existingSchedule  Current milestone list (used in manual mode)
 * @param scheduleRules Optional rules from AppPreferences. Null = manual/proportional.
 */
import { calculateQuotationTotals } from './pricingEngine';

export function calculateProjectFinancials(
  sections: Array<{ sectionSubtotal?: number; gstEnabled?: boolean }>,
  financials: Financials,
  existingSchedule: PaymentMilestone[],
  scheduleRules?: PaymentScheduleRule[] | null,
): FinancialCalculationResult {
  const taxEnabled = sections.some(s => s.gstEnabled);
  const { discountType, discountValue, taxRate, paymentScheduleAuto } = financials;

  const sectionSubtotals = sections.map(s => s.sectionSubtotal || 0);

  const {
    subtotal,
    taxAmount,
    discountAmount,
    taxableAmount,
    grandTotal
  } = calculateQuotationTotals(
    sectionSubtotals,
    taxRate,
    taxEnabled,
    discountValue,
    discountType
  );

  // ── Payment schedule ───────────────────────────────────────────────────────
  const isAuto = paymentScheduleAuto !== false;
  const schedule = buildPaymentSchedule(
    grandTotal,
    existingSchedule,
    isAuto,
    scheduleRules ?? null,
  );

  // Floating-point rounding correction on the last milestone
  if (schedule.length > 0 && grandTotal > 0) {
    const amountSum = schedule.reduce((s, m) => s + m.amount, 0);
    const pctSum    = schedule.reduce((s, m) => s + m.percentage, 0);
    const last = schedule[schedule.length - 1];
    if (Math.abs(grandTotal - amountSum) < 0.1) {
      last.amount     = Number((last.amount + (grandTotal - amountSum)).toFixed(2));
      last.percentage = Number((last.percentage + (100 - pctSum)).toFixed(4));
    }
  }

  return { subtotal, taxAmount, discountAmount, taxableAmount, grandTotal, paymentSchedule: schedule };
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function buildPaymentSchedule(
  grandTotal: number,
  existing: PaymentMilestone[],
  isAuto: boolean,
  rules: PaymentScheduleRule[] | null,
): PaymentMilestone[] {
  if (grandTotal <= 0) {
    return existing.map(m => ({ ...m, amount: 0, percentage: 0 }));
  }

  // MANUAL MODE — preserve percentages, recalculate amounts only
  if (!isAuto) {
    return existing.map(m => ({
      ...m,
      amount: Number(((m.percentage * grandTotal) / 100).toFixed(2)),
    }));
  }

  // RULE-BASED AUTO MODE — find applicable rule bracket
  if (rules && rules.length > 0) {
    const rule = findApplicableRule(grandTotal, rules);
    if (rule) {
      return rule.milestones.map((def, i) => {
        const base = (def.percentage * grandTotal) / 100;
        const amount = def.fixedCap !== undefined
          ? Math.min(base, def.fixedCap)
          : base;
        const id = existing[i]?.id || crypto.randomUUID();
        return {
          id,
          milestoneName: def.name,
          percentage: def.percentage,
          amount: Number(amount.toFixed(2)),
          dueStage: existing[i]?.dueStage,
        };
      });
    }
  }

  // PROPORTIONAL FALLBACK — keep existing milestones, recalculate amounts
  const base = existing.length > 0 ? existing : DEFAULT_PAYMENT_MILESTONES.map(m => ({
    ...m,
    id: crypto.randomUUID(),
    amount: 0,
  }));

  return base.map(m => ({
    ...m,
    amount: Number(((m.percentage * grandTotal) / 100).toFixed(2)),
  }));
}

function findApplicableRule(
  grandTotal: number,
  rules: PaymentScheduleRule[],
): PaymentScheduleRule | null {
  // Sort ascending by upTo so we find the first bracket the total fits into
  const sorted = [...rules].sort((a, b) => (a.upTo ?? Infinity) - (b.upTo ?? Infinity));
  return sorted.find(r => r.upTo === undefined || grandTotal <= r.upTo) ?? null;
}
